<template>
  <button
    :class="[
      'add-to-cart',
      {
        'add-to-cart--added': isAddedOffer,
        'add-to-cart--disabled': disabled
      },
      themeClass
    ]"
    :loading="isAdding"
    :disabled="disabled"
    @click="handleAddToCart"
  >
    <span
      v-if="theme !== 'inline'"
      class="add-to-cart__icon"
      :class="{
        'add-to-cart__icon': theme === 'default',
        'icon-cart': !isAddedOffer,
        'icon-cart-full': isAddedOffer
      }"
    />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { modalCartId } from '@/constants/modalNames'
import type { ProductCatalog } from '~/types/product'

type Props = {
  offerId: string
  offerCode: string
  product: ProductCatalog
  theme?: 'default' | 'inline'
  showCart?: boolean
  disabled?: boolean
}
type Emits = {
  (e: 'added-to-cart'): void
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'default',
  showCart: false
})
const emit = defineEmits<Emits>()

const commonMetricsStore = useCommonMetricsStore()
const cartStore = useCartStore()
const { $vfm } = useNuxtApp()

const themeClass = `add-to-cart--${props.theme}`
const isAdding = shallowRef(false)

const isAddedOffer = computed(() => cartStore.addedOffer(props.offerId))

const handleAddToCart = () => {
  isAdding.value = true

  if (isAddedOffer.value) {
    $vfm.open(modalCartId)
    return
  }

  commonMetricsStore.addMetricEvent({
    element: 'ms[product-card-add-to-card]',
    event_type: 'click',
    event_content: props.offerCode
  })

  cartStore.addToCart({
    offerId: props.offerId,
    offerCode: props.offerCode,
    offerName: props.product.name,
    categoryName: props.product.category.name,
    price: props.product.offer_main?.price_promo || props.product.offer_main?.price || 0,
    showCart: props.showCart
  })
    .finally(() => {
      isAdding.value = false
      emit('added-to-cart')
    })
}

</script>

<style lang="scss" scoped>
.add-to-cart {
  $root: &;
  cursor: pointer;
  background-color: transparent;
  transition: background-color 0.2s, border-color 0.2s;
  outline: none;
  border: none;

  &--default {
    height: 4rem;
    width: 4rem;
    border-radius: 8px;
    border: none;
    font-size: 1.8rem;
    display: flex;
    justify-content: center;
    align-items: center;

    @media only screen and (max-width: 1024px) {
      font-size: 2.6rem;
    }

    #{$root}__icon {
      color: $accent-color;
      font-size: 1.6rem;

      @media only screen and (max-width: 740px) {
        font-size: 2.2rem;
      }

      // &.icon-cart-filled {
      //   font-size: 2.6rem !important;

      //   @media only screen and (max-width: 740px) {
      //     font-size: 3.6rem !important;
      //   }
      // }
    }

    @media only screen and (min-width: 1024px) {
      border: solid 0.1rem $border-color;

      &:hover {
        background-color: $accent-color;
        border-color: $accent-color;

        #{$root}__icon {
          color: #fff;
        }
      }

      &#{$root}--added {
        border: solid 0.2rem $accent-color;
      }

      &#{$root}--disabled {
        opacity: 0.6;
        cursor: default;
        pointer-events: none;
      }
    }
  }

  &--inline {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
  }
}
</style>
